import { AppDispatch } from "@/store";

/**
 * Returns true if the given section is already present in:
 * - any selectedSection with job_type === "denmark" (direct section or its multi_publish_targets)
 * - the currently active chip (direct section or its multi_publish_targets in selectedArticle)
 */
export const isSectionAlreadySelected = (
  section: Record<string, unknown>,
  selectedSections: Record<string, unknown>[],
  selectedArticle: Record<string, unknown>[],
  activeChip: number | null
): boolean => {
  const incomingSectionId = Number(section?.section ?? section?.value);
  if (!incomingSectionId) return false;

  const isInMultiPublishTargets = (item: Record<string, unknown>): boolean => {
    const targets = item?.multi_publish_targets;
    if (!Array.isArray(targets)) return false;
    return targets.some(
      (t: Record<string, unknown>) => Number(t?.section) === incomingSectionId
    );
  };

  // Check selectedSections filtered to denmark job_type
  const denmarkSections = selectedSections.filter(
    (s) => s?.job_type === "denmark"
  );
  for (const item of denmarkSections) {
    if (Number(item?.section) === incomingSectionId) return true;
    if (isInMultiPublishTargets(item)) return true;
  }

  // Check the active chip's article and its multi_publish_targets
  if (activeChip != null) {
    if (Number(activeChip) === incomingSectionId) return true;
    const activeChipArticle = selectedArticle.find(
      (item) => Number(item?.section) === Number(activeChip)
    );
    if (activeChipArticle && isInMultiPublishTargets(activeChipArticle))
      return true;
  }

  return false;
};
import {
  upsertSectionInPriorityList,
  setPriorityFetching,
} from "@/store/media/media.slice";
import { fetchPriorityListApi } from "@/database/media/api";
import { PrioritySectionEntry } from "./DenmarkEditContent";

/**
 * On save failure: silently re-fetch the server's priority list for a section
 * and sync it back into the Redux store.
 * If selectedChipId is not present in the fresh list it is prepended so the entry
 * stays ready for a retry save.
 * deletedList is cleared automatically by upsertSectionInPriorityList.
 *
 * This is fire-and-forget — the UI never shows a loading state for it.
 */
export const refetchAndSync = (
  dispatch: AppDispatch,
  entry: PrioritySectionEntry,
  selectedChipId: number | string,
  selectedChipTitle: string
): void => {
  const host =
    entry.priorityList?.priorityList?.priorityListEntity?.holder?.hostId ?? 0;

  dispatch(setPriorityFetching(true));
  fetchPriorityListApi({
    host: Number(host),
    email: "",
    msid: 0,
    parentId: entry.sectionId,
  })
    .then((fetchResponse) => {
      dispatch(setPriorityFetching(false));
      if (fetchResponse.status !== 200 || !fetchResponse.data) return;

      const freshData = fetchResponse.data;
      const rawList: any[] =
        freshData?.priorityList?.priorityListEntity?.priorityList ?? [];
      const numericSelectedChipId = Number(selectedChipId);
      const hasMediaContext =
        Boolean(selectedChipId) && numericSelectedChipId !== 0;
      const alreadyPresent =
        !hasMediaContext ||
        rawList.some((p: any) => p.msid === numericSelectedChipId);

      let finalData = freshData;
      let newlyAddedList: any[] = [];

      if (!alreadyPresent) {
        const newItem = {
          msid: numericSelectedChipId,
          hostId: Number(host),
          priorityTitle: selectedChipTitle,
          priorityType: 0,
          addedAtTimestampEpoch: Date.now(),
          readonly: {
            status: "ACTIVE",
            entityType: "MEDIAVIDEO",
            publishDate: Date.now(),
          },
        };
        newlyAddedList = [newItem];
        finalData = {
          ...freshData,
          priorityList: {
            ...freshData?.priorityList,
            priorityListEntity: {
              ...freshData?.priorityList?.priorityListEntity,
              priorityList: [newItem, ...rawList],
            },
          },
        };
      }

      dispatch(
        upsertSectionInPriorityList({
          sectionId: entry.sectionId,
          sectionName: entry.sectionName,
          priorityList: finalData,
          newlyAddedList,
        })
      );
    })
    .catch(() => {
      dispatch(setPriorityFetching(false));
    });
};
